/**
 * Broadcast evaluation — the Extraliga stream-grading workflow.
 *
 * Two layers live in one document and must never be confused:
 *
 * - the **measured layer** (`measurements`, `facts`, `events`, the match
 *   identity) is written only by `sle publish` in the staylive-livestream-
 *   evaluation repo. Nothing here may write it.
 * - the **decision layer** (`state`, `declarations`, `reward`, plus the
 *   `decisions` and `comments` subcollections) is written only here. `sle
 *   publish` reads it to know what it must not touch.
 *
 * The tool re-derives every verdict on every run, so a rule change re-scores
 * the whole season. A game the producer has already seen is therefore frozen
 * on the tool's side and a fresh measurement only shows up as
 * `measurementDrift` for an admin to look at.
 */

/** Where a game sits in the approval workflow. */
export type EvaluationState = 'toDecide' | 'toApprove' | 'returned' | 'approved';

/**
 * What a criterion does to the money.
 *
 * - `gate` — an absolute condition. Missing means the whole game pays nothing.
 * - `penalty` — cuts a share off the production fee. Technical parameters sit
 *   here rather than in `gate`: a stream below 1080p is a real shortfall, but
 *   the commentary and the replay it still delivered are not worth less for it.
 * - `paid` — has its own rate.
 * - `info` — measured and shown, but the contract does not pay for it.
 * - `declared` — not measurable from the picture; the producer declares it and
 *   an admin confirms.
 */
export type CriterionRole = 'gate' | 'penalty' | 'paid' | 'info' | 'declared';

export type CriterionGroup = 'mandatory' | 'sponsor' | 'script' | 'rated';

export interface CriterionDef {
  id: string;
  label: string;
  group: CriterionGroup;
  role: CriterionRole;
  /** Column name the tool knows it by; null for a declared criterion. */
  source: string | null;
}

/** Rates in whole crowns per game, straight out of the contract. */
export interface EvaluationRates {
  production: number;
  /** Per video, not per game — the value of `extraVideos` is a count. */
  extraVideo: number;
  commentary: number;
  replay: number;
  operatedCamera: number;
}

/** Shares cut off the production fee. Nothing else is affected by them. */
export interface EvaluationPenalties {
  /** Per sponsor message that never ran. */
  sponsorMessage: number;
  /** Taken off the production fee when the opener never ran. */
  opener: number;
  /** Only one of the two lineup graphics was shown. */
  lineupsPartial: number;
  /** Neither was shown. */
  lineupsMissing: number;
  /**
   * Technical parameters, scaled by how far short the broadcast fell. It is
   * the largest single cut and the only one an admin can dial: the fee is five
   * sponsor messages at ten percent, a script worth at most ten, and this
   * forty for everything else the production is expected to get right.
   *
   * Published from the tool's `config.toml`; older season documents predate it
   * and read as undefined, which is why every use has to cope with a missing
   * value instead of assuming the rate is there.
   */
  technical?: number;
}

/**
 * A sample of the broadcast's sound, uploaded next to the stills. Today there
 * is one kind, `lineups` — the minute around the lineup presentation, where a
 * commentator is certain to be talking if the broadcast has one.
 */
export interface EvaluationAudioClip {
  kind: string;
  file: string;
  storageName: string;
  storagePath?: string;
  start?: number;
  end?: number;
  /**
   * Some productions play the lineup graphic as a clip with no sound at all,
   * so the sample was moved to the first stretch that has any — the caption
   * says so, otherwise a minute of digital silence would read as "nobody is
   * commentating".
   */
  movedPastSilence?: boolean;
}

/** `evaluations/{season}` — the rate card the web computes from. */
export interface EvaluationSeason {
  season: string;
  currency: string;
  rates: EvaluationRates;
  penalties: EvaluationPenalties;
  criteria: CriterionDef[];
  evalVersion: string;
  featureVersion: string;
  publishedAt?: any;
}

/** One criterion as the tool measured it. */
export interface Measurement {
  /** 1 met, 0 not met, a fraction where the tool measured one, null unknown. */
  value: number | null;
  /** The tool measured but refuses to decide — this is what the wizard is for. */
  review: boolean;
  note: string;
  /** Seconds into the recording where the finding sits. */
  at: number | null;
}

/** A still from the recording, kept as proof of a finding. */
export interface EvidenceEvent {
  kind: string;
  slot: string | null;
  start: number;
  end: number;
  duration: number;
  evidence: string;
  storageName: string;
  /** Path in Firebase Storage; null when the upload did not happen. */
  storagePath: string | null;
  /**
   * Which criterion the still proves, where order alone cannot say. Set by the
   * tool on the two lineup graphics: T1 is the presentation where the guests
   * bat, M1 the home one, and the tool reads that off the team colour in the
   * graphic rather than guessing from what came first.
   */
  criterion?: string;
  /** The team batting in that presentation, for the caption. */
  team?: string;
}

/** Measured numbers behind the verdicts, so a dispute rarely needs the video. */
export interface EvaluationFacts {
  format?: string;
  cameraPositions?: number;
  cameraShares?: number[];
  /**
   * Share of the recording in which the game was actually running — the tool
   * reads it off the scoreboard graphic. Commentary, ambient sound and the
   * camera count are measured over that stretch only, so a low number says how
   * much the verdicts had to go on.
   */
  playShare?: number;
  speechCoverage?: number;
  speechRunMedianS?: number;
  soundLevelDb?: number;
  highestInning?: number;
  finalHome?: number;
  finalAway?: number;
  outageCount?: number;
  outageLongestS?: number;
  outageTotalS?: number;
  coverage?: number;
}

/** What the producer declares about the game; an admin confirms it. */
export interface EvaluationDeclarations {
  /** 0 none, 0.5 an operated camera, 1 an operated camera on live play. */
  operatedCamera?: number;
  declaredBy?: string;
  declaredByName?: string;
  declaredAt?: any;
  confirmedBy?: string;
  /** Absent on confirmations made before it was recorded; fall back to the uid. */
  confirmedByName?: string;
  confirmedAt?: any;
  /**
   * An admin does not accept the declaration. It never moves money by itself —
   * on an approved game the amount is agreed — so this is the record of the
   * disagreement, and `disputeNote` says what it is about. The same text is
   * posted to `comments` so the producer sees it where every other objection
   * on this game already lives.
   */
  disputedBy?: string;
  disputedByName?: string;
  disputedAt?: any;
  disputeNote?: string;
}

/** One line of the payment, in whole crowns. */
export interface RewardLine {
  id: string;
  label: string;
  amount: number;
  detail?: string;
}

/** The payment as it was computed. Stored on approval, so it can be re-read. */
export interface Reward {
  total: number;
  lines: RewardLine[];
  /** An absolute condition failed, so nothing is paid. */
  blocked: boolean;
  /** Criterion ids that blocked it. */
  blockedBy: string[];
  /** Criteria still without a value; the game cannot be released. */
  pending: string[];
  currency: string;
  /** Rates it was computed from — the contract may change between seasons. */
  rates?: EvaluationRates;
  computedAt?: any;
}

/** An admin's ruling on a criterion the tool would not decide. */
export interface EvaluationDecision {
  /** Same as the document id; kept in the body so a list can be read flat. */
  criterion: string;
  value: number | null;
  note: string;
  by: string;
  byName?: string;
  at: any;
  /** What the tool said, so the change stays visible. */
  original: number | null;
  evalVersion?: string;
}

/** A producer's objection, or an admin's answer to one. */
export interface EvaluationComment {
  id?: string;
  /** Which criterion it is about; null when it is about the game as a whole. */
  criterion: string | null;
  text: string;
  by: string;
  byName?: string;
  at: any;
  resolvedBy?: string;
  resolvedAt?: any;
}

/** Fresh measurement that disagrees with what a producer already approved. */
export interface MeasurementDrift {
  detectedAt: any;
  evalVersion: string;
  changes: { criterion: string; from: number | null; to: number | null }[];
}

/** `evaluations/{season}/games/{videoId}`. */
export interface EvaluationGame {
  // --- measured layer, written only by `sle publish` ---
  videoId: number;
  matchNo: number | null;
  name: string;
  date: string;
  home: string | null;
  away: string | null;
  competition: string;
  venue: string | null;
  channelId: number;
  producerName: string;
  durationS: number;
  scheduledDurationS: number | null;
  dashboardUrl: string;
  /** Also broadcast on ČT Sport+ — the commentary is then not the producer's to bill. */
  ctSportPlus: boolean;
  evalVersion: string;
  featureVersion: string;
  analysedAt: string;
  publishedAt?: any;
  measurements: { [criterionId: string]: Measurement };
  facts: EvaluationFacts;
  events: EvidenceEvent[];
  audioClips?: EvaluationAudioClip[];
  /**
   * Where the recording sits on the admin's own disk. Published so a disputed
   * game can be opened in a player without hunting across the drives; it is
   * shown to admins only, because it means nothing to anybody else.
   */
  localFile?: string;
  measurementDrift?: MeasurementDrift;

  // --- decision layer, written only by this app ---
  state: EvaluationState;
  /**
   * Effective values for the criteria an admin has ruled on, folded in as each
   * ruling is made. The `decisions` subcollection keeps who ruled and why; this
   * is here so a list screen can tell a settled game from an open one without
   * opening a subcollection per game.
   */
  values?: { [criterionId: string]: number };
  valuesAt?: any;
  declarations?: EvaluationDeclarations;
  releasedAt?: any;
  releasedBy?: string;
  approvedAt?: any;
  approvedBy?: string;
  approvedByName?: string;
  returnedAt?: any;
  returnedBy?: string;
  /**
   * An admin pulled the game back out of the producer's hands to re-rule it.
   * Kept as a stamp rather than a state of its own: the game really is back in
   * `toDecide`, and the producer asking why it vanished deserves a name.
   */
  withdrawnAt?: any;
  withdrawnBy?: string;
  withdrawnByName?: string;
  reward?: Reward;
}

/** `producers/{channelId}` — a production company, keyed by its StayLive channel. */
export interface Producer {
  channelId: number;
  name: string;
  active: boolean;
}

/** How the contract groups the criteria, in the order it lists them. */
export const GROUP_LABELS: { [group in CriterionGroup]: string } = {
  mandatory: 'Absolute conditions',
  sponsor: 'Sponsor messages',
  script: 'Script',
  rated: 'Paid separately',
};

export const GROUP_ORDER: CriterionGroup[] = ['mandatory', 'sponsor', 'script', 'rated'];

export const STATE_LABELS: { [state in EvaluationState]: string } = {
  toDecide: 'To decide',
  toApprove: 'Waiting for producer',
  returned: 'Returned',
  approved: 'Approved',
};
